import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Send, Sparkles, Image as ImageIcon, Settings, Loader2, CheckCircle2 } from "lucide-react";

type Tab = "overview" | "requests" | "generator" | "library" | "settings";

interface Review {
  id: string;
  author: string;
  rating: number;
  text: string;
  source: string;
  date: string;
  responded: boolean;
}

const REVIEWS: Review[] = [
  { id: "1", author: "Margaret S.", rating: 5, text: "The caregivers have been a blessing for my mother. Compassionate and professional.", source: "Google", date: "2 days ago", responded: true },
  { id: "2", author: "Robert T.", rating: 5, text: "Excellent communication. They truly understand dementia care.", source: "Google", date: "1 week ago", responded: true },
  { id: "3", author: "Linda K.", rating: 4, text: "Very good service overall, occasional scheduling hiccups.", source: "Facebook", date: "2 weeks ago", responded: false },
  { id: "4", author: "James W.", rating: 5, text: "Highly recommend. They go above and beyond.", source: "Google", date: "3 weeks ago", responded: true },
];

export default function ReviewsReputation() {
  const [tab, setTab] = useState<Tab>("overview");
  const [reqEmail, setReqEmail] = useState("");
  const [assetOpen, setAssetOpen] = useState(false);
  const [assetReview, setAssetReview] = useState<Review | null>(null);
  const [assetGenerating, setAssetGenerating] = useState(false);
  const [assetStep, setAssetStep] = useState<"idle" | "designing" | "rendering" | "done">("idle");

  const sendRequest = () => {
    if (!reqEmail.trim()) return toast.error("Enter an email");
    toast.success(`Review request sent to ${reqEmail}`);
    setReqEmail("");
  };

  const openAsset = (r: Review) => {
    setAssetReview(r);
    setAssetOpen(true);
    setAssetStep("idle");
  };

  const generateAsset = () => {
    setAssetGenerating(true);
    setAssetStep("designing");
    setTimeout(() => setAssetStep("rendering"), 1200);
    setTimeout(() => {
      setAssetStep("done");
      setAssetGenerating(false);
      toast.success("Social asset ready");
    }, 2800);
  };

  const avgRating = (REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length).toFixed(1);

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "overview", label: "Overview", icon: Star },
    { key: "requests", label: "Requests", icon: Send },
    { key: "generator", label: "Asset Generator", icon: Sparkles },
    { key: "library", label: "Asset Library", icon: ImageIcon },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Average rating</div><div className="text-3xl font-bold text-navy mt-1 flex items-center gap-2">{avgRating} <Star className="h-6 w-6 fill-gold text-gold" /></div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Total reviews</div><div className="text-3xl font-bold text-navy mt-1">{REVIEWS.length * 12}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Response rate</div><div className="text-3xl font-bold text-navy mt-1">87%</div></CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Recent reviews</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {REVIEWS.map((r) => (
                <div key={r.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{r.author}</span>
                      <Badge variant="outline" className="text-xs">{r.source}</Badge>
                      <div className="flex">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-gold text-gold" />)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{r.date}</span>
                      <Button size="sm" variant="outline" onClick={() => openAsset(r)}>Make asset</Button>
                    </div>
                  </div>
                  <p className="text-sm mt-2 text-foreground">{r.text}</p>
                  {!r.responded && <Button size="sm" variant="outline" className="mt-2">Reply</Button>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "requests" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Send a review request</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Client email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} />
              <Button onClick={sendRequest} className="bg-gold text-gold-foreground hover:bg-gold/90"><Send className="h-4 w-4" /> Send</Button>
            </div>
            <p className="text-xs text-muted-foreground">Sends a branded email with one-click links to Google &amp; Facebook.</p>
          </CardContent>
        </Card>
      )}

      {tab === "generator" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Generate a social asset from a review</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {REVIEWS.filter(r => r.rating === 5).map((r) => (
              <button key={r.id} onClick={() => openAsset(r)} className="text-left p-4 border rounded-lg hover:border-gold">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{r.author}</span>
                  <div className="flex">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-gold text-gold" />)}</div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{r.text}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === "library" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Saved assets</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-square rounded-lg border-2 border-gold bg-cream flex items-center justify-center text-center p-4">
                  <div>
                    <Star className="h-6 w-6 fill-gold text-gold mx-auto mb-2" />
                    <p className="text-xs italic text-navy">"They go above and beyond."</p>
                    <p className="text-xs font-bold text-navy mt-2">— James W.</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "settings" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Reputation settings</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <span>Auto-request reviews 7 days after service</span>
              <Badge className="bg-success/10 text-success">On</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <span>Notify me when a new review arrives</span>
              <Badge className="bg-success/10 text-success">On</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={assetOpen} onOpenChange={setAssetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate social asset</DialogTitle></DialogHeader>
          {assetReview && (
            <div className="space-y-3">
              <div className="aspect-square rounded-lg border-2 border-gold bg-cream flex items-center justify-center text-center p-6">
                <div>
                  <div className="flex justify-center mb-2">{Array.from({ length: assetReview.rating }).map((_, i) => <Star key={i} className="h-5 w-5 fill-gold text-gold" />)}</div>
                  <p className="italic text-navy">"{assetReview.text}"</p>
                  <p className="font-bold text-navy mt-3">— {assetReview.author}</p>
                </div>
              </div>
              {assetGenerating && (
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2">{assetStep === "designing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 text-success" />} Designing layout</div>
                  <div className="flex items-center gap-2">{assetStep === "rendering" ? <Loader2 className="h-4 w-4 animate-spin" /> : assetStep === "done" ? <CheckCircle2 className="h-4 w-4 text-success" /> : <div className="h-4 w-4" />} Rendering image</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetOpen(false)}>Close</Button>
            <Button onClick={generateAsset} disabled={assetGenerating} className="bg-gold text-gold-foreground hover:bg-gold/90">
              {assetGenerating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating</> : <><Sparkles className="h-4 w-4" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
